import { useState } from 'react';
import { FadeIn, FadeInStagger, FadeInStaggerItem } from '../../components/ui/AnimatedSection';
import { useTranslation } from 'react-i18next';

export default function TimelineSection() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const { t } = useTranslation();
  const steps = t('home.timeline.items', { returnObjects: true }) as Array<{ title: string; desc: string }>;

  return (
    <>
      <section className="py-32 bg-white text-black-deep relative overflow-hidden" id="methode">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 opacity-40 pointer-events-none">
          <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-gold-200/50 blur-[120px]"></div>
          <div className="absolute top-[60%] -right-[10%] w-[40%] h-[60%] rounded-full bg-gold-100/50 blur-[100px]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <FadeIn className="text-center mb-24">
            <span className="text-gold-600 tracking-[0.2em] text-xs font-bold uppercase mb-4 block">{t('home.timeline.tag')}</span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-black-deep mb-8">{t('home.timeline.title')}</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-gold-500 to-gold-400 mx-auto rounded-full"></div>
          </FadeIn>

          <FadeInStagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative z-10">
            {steps.map((step, index) => (
              <FadeInStaggerItem 
                key={index} 
                className="h-full"
              >
                <div
                  className="relative group cursor-pointer h-full"
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-gold-100/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl blur-xl"></div>
                  <div className="relative h-full p-8 rounded-2xl border border-gray-100 bg-white hover:border-gold-300 transition-all duration-500 hover:-translate-y-2 overflow-hidden flex flex-col shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgba(201,152,85,0.15)]">
                  {/* Background number watermark */}
                  <div className="absolute -right-4 -bottom-4 text-9xl font-serif font-bold text-black-deep/[0.02] select-none pointer-events-none group-hover:text-gold-500/[0.05] transition-colors duration-700">
                    0{index + 1}
                  </div>
                  
                  <div className="w-14 h-14 bg-gradient-to-br from-gold-400 to-gold-600 rounded-full flex items-center justify-center mb-8 shadow-[0_4px_15px_rgba(201,152,85,0.3)] group-hover:shadow-[0_8px_25px_rgba(201,152,85,0.5)] transition-all duration-500 relative z-10">
                    <span className="font-serif text-white text-xl font-medium">0{index + 1}</span>
                  </div>
                  
                  <h3 className="text-2xl font-serif text-black-deep mb-4 group-hover:text-gold-600 transition-colors duration-300 relative z-10">
                    {step.title}
                  </h3>
                  
                  <p className="text-gray-500 font-light text-sm leading-relaxed flex-grow relative z-10">
                    {step.desc}
                  </p>
                  
                  {/* Decorative line */}
                  <div className="w-0 h-0.5 bg-gradient-to-r from-gold-400 to-gold-600 mt-8 group-hover:w-full transition-all duration-700 ease-in-out relative z-10"></div>
                </div>
                </div>
              </FadeInStaggerItem>
            ))}
          </FadeInStagger>
        </div>
      </section>

      {/* Central Spotlight Overlay for Hovered Card */}
      <div 
        className={`fixed inset-0 z-[9999] pointer-events-none flex items-center justify-center transition-all duration-500 ${
          hoveredIndex !== null ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <div className="absolute inset-0 bg-white/70 backdrop-blur-md"></div>
        
        <div className={`relative z-10 w-full max-w-2xl mx-4 p-12 rounded-3xl border border-gold-200 bg-white shadow-[0_30px_100px_rgba(201,152,85,0.3)] transition-all duration-500 transform overflow-hidden ${hoveredIndex !== null ? 'scale-100 translate-y-0 opacity-100' : 'scale-90 translate-y-12 opacity-0'}`}>
          {hoveredIndex !== null && (
            <>
             <div className="absolute -right-12 -bottom-12 text-[15rem] font-serif font-bold text-black-deep/[0.03] select-none pointer-events-none">
               0{hoveredIndex + 1}
             </div>
             
             <div className="w-24 h-24 bg-gradient-to-br from-gold-400 to-gold-600 rounded-full flex items-center justify-center mb-10 shadow-[0_10px_30px_rgba(201,152,85,0.5)] relative z-10">
               <span className="font-serif text-white text-4xl font-medium">0{hoveredIndex + 1}</span>
             </div>
             
             <h3 className="text-4xl md:text-5xl font-serif text-gold-600 mb-8 relative z-10">
               {steps[hoveredIndex].title}
             </h3>
             
             <p className="text-gray-600 font-light text-xl md:text-2xl leading-relaxed relative z-10">
               {steps[hoveredIndex].desc}
             </p>
            </>
          )}
        </div>
      </div>
    </>
  );
}
